import {
  capTotalReference,
  reconciliationAgainstCapReference,
  type CapTotalMetric,
} from './capTotalReference';
import type { ChartDisplayMode } from './elecNonresidentialChartSegments';
import { buildElecTotalChartSegments } from './elecTotalChartSegments';
import { buildGasTotalChartSegments } from './gasTotalChartSegments';
import type { CanonicalEnergyActivityYear } from './types';

function fmtActivity(n: number, unit: string): string {
  return `${Math.round(n).toLocaleString()} ${unit}`;
}

function fmtMt(n: number): string {
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} MT CO₂e`;
}

function activityBasisLabel(basis: string): string {
  return basis === 'published' ? 'Published CAP activity' : 'Derived from published CAP emissions and factors';
}

export function capReferenceTooltipLines(
  metric: CapTotalMetric,
  year: number,
  mode: ChartDisplayMode,
  experimentalDisplayTotal: number
): string[] {
  const ref = capTotalReference(metric, year);
  if (!ref) return [];
  const capDisplay =
    mode === 'emissions' ? fmtMt(ref.emissionsValue) : fmtActivity(ref.activityValue, ref.activityUnit);
  const recon = reconciliationAgainstCapReference(metric, year, experimentalDisplayTotal, mode);
  const lines = [
    `Year: ${year}`,
    `CAP reference: ${capDisplay}`,
    `Unit: ${mode === 'emissions' ? ref.emissionsUnit : ref.activityUnit}`,
    `Activity status: ${activityBasisLabel(ref.activityBasis)}`,
    `Emissions status: Published CAP emissions`,
    `Source: Beverly Hills CAP inventory (${ref.sourceId})`,
    ref.scopeNotes,
  ];
  if (metric === 'electricity' && ref.tdLossesMtExcluded != null) {
    lines.push(
      `Published CAP T&D losses (excluded from this bar): ${fmtMt(ref.tdLossesMtExcluded)}`,
      'Direct electricity emissions; CAP T&D losses are excluded to match the metered building-electricity scope.'
    );
  }
  if (metric === 'natural_gas' && mode === 'activity') {
    lines.push(
      `Published CAP natural-gas emissions: ${fmtMt(ref.publishedEmissionsMt)}`,
      'Therms derived from published CAP natural-gas emissions divided by the published CAP factor; utility therm totals are redacted.'
    );
  }
  if (metric === 'natural_gas' && mode === 'emissions') {
    lines.push(`Published CAP natural-gas emissions: ${fmtMt(ref.publishedEmissionsMt)}`);
  }
  if (recon) {
    lines.push(
      `Experimental total: ${mode === 'emissions' ? fmtMt(experimentalDisplayTotal) : fmtActivity(experimentalDisplayTotal, ref.activityUnit)}`,
      `Difference (experimental − CAP): ${recon.delta.toLocaleString(undefined, { maximumFractionDigits: 4 })}`,
      `Percent difference: ${Number.isFinite(recon.pct) ? recon.pct.toFixed(4) : '—'}%`,
      `Reconciliation: ${recon.passes ? 'pass (≈0 at anchor)' : 'review delta'}`
    );
  }
  return lines;
}

export function elecTotalYearTooltipLines(
  row: CanonicalEnergyActivityYear,
  mode: ChartDisplayMode
): string[] {
  const seg = buildElecTotalChartSegments(row, mode);
  if (seg.missingComponent) {
    return ['Missing canonical electricity components — cannot display stack.'];
  }
  const unit = mode === 'emissions' ? 'MT CO₂e' : 'kWh';
  const fmt = (n: number) =>
    mode === 'emissions' ? fmtMt(n) : `${Math.round(n).toLocaleString()} kWh`;
  const lines = [
    'Experimental residential + nonresidential estimate',
    `Experimental residential: ${fmt(seg.residential)}`,
    `Experimental nonresidential: ${fmt(seg.nonresidential)}`,
    `Experimental total: ${fmt(seg.canonicalTotal)} ${mode === 'emissions' ? '' : unit}`.trim(),
  ];
  if (row.year === 2015 || row.year === 2019) {
    lines.push('', '— CAP reference bar (adjacent) —');
    lines.push(...capReferenceTooltipLines('electricity', row.year, mode, seg.canonicalTotal));
  }
  return lines;
}

export function gasTotalYearTooltipLinesRevised(
  row: CanonicalEnergyActivityYear,
  mode: ChartDisplayMode,
  options?: { includeIndustrial?: boolean }
): string[] {
  const seg = buildGasTotalChartSegments(row, mode, options);
  if (seg.missingComponent) {
    return ['Missing canonical gas components — cannot display stack.'];
  }
  const unit = mode === 'emissions' ? 'MT CO₂e' : 'therms';
  const fmt = (n: number) =>
    mode === 'emissions' ? fmtMt(n) : `${Math.round(n).toLocaleString()} therms`;
  const lines = options?.includeIndustrial
    ? [
        'Experimental residential + commercial + industrial (when reported)',
        `Experimental residential: ${fmt(seg.residential)}`,
        `Experimental commercial: ${fmt(seg.commercialCategory)}`,
        seg.industrialCategory != null
          ? `Experimental industrial: ${fmt(seg.industrialCategory)}`
          : 'Experimental industrial: not reported for this year',
        `Experimental total: ${fmt(seg.canonicalTotal)}`,
      ]
    : [
        'Experimental residential + commercial-category estimate',
        `Experimental residential: ${fmt(seg.residential)}`,
        `Experimental commercial category: ${fmt(seg.commercialCategory)}`,
        `Experimental total: ${fmt(seg.canonicalTotal)}`,
      ];
  if (row.year === 2015 || row.year === 2019) {
    lines.push('', '— CAP reference bar (adjacent) —');
    lines.push(...capReferenceTooltipLines('natural_gas', row.year, mode, seg.canonicalTotal));
  } else if (row.year >= 2021 && row.year <= 2023) {
    lines.push('SoCalGas reported categories; sum treated as total under negligible-unreported assumption.');
  }
  return lines;
}
